import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  Optional,
  Renderer2,
  Self,
  ViewChild,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { ControlValueAccessor, FormControl } from '@ngneat/reactive-forms';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';

import { User } from '@digital-platform/people/domain';
import { PeopleFacadeService } from '@digital-platform/people/services';

@Component({
  selector: 'digital-platform-coordinators-drop-down',
  templateUrl: './coordinators-drop-down.component.html',
  styleUrls: ['./coordinators-drop-down.component.scss'],
})
export class CoordinatorsDropDownComponent
  implements OnInit, OnDestroy, ControlValueAccessor
{
  @ViewChild('selectCoordinatorRef', { read: ElementRef })
  selectCoordinatorRef!: ElementRef;
  coordinators$: Observable<User[]>;
  control = new FormControl<User>();
  public onTouched: () => void;
  private destroy$ = new Subject<void>();

  constructor(
    @Optional() @Self() public controlDir: NgControl,
    private facade: PeopleFacadeService,
    private renderer: Renderer2
  ) {
    if (controlDir !== null) {
      controlDir.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    this.coordinators$ = this.facade.coordinators$.pipe(
      takeUntil(this.destroy$),
      map((d) => d.sort((a, b) => a.name.localeCompare(b.name))),
      tap((coordinators) => {
        const selectedCoordinator = coordinators.find(
          (coordinator) => coordinator.name === this.controlDir.value?.name
        );
        this.control.setValue(selectedCoordinator, {
          emitEvent: false,
        });
      })
    );
  }

  ngOnDestroy() {
    this.destroy$.next();

    this.destroy$.complete();
  }

  writeValue(value: User): void {
    if (value) {
      this.coordinators$ = this.facade.coordinators$.pipe(
        takeUntil(this.destroy$),
        map((d) => d.sort((a, b) => a.name.localeCompare(b.name))),
        tap((coordinators) => {
          const selectedCoordinator = coordinators.find(
            (coordinator) => coordinator.name === this.controlDir.value?.name
          );
          this.control.setValue(selectedCoordinator, {
            emitEvent: false,
          });
        })
      );
    }
  }

  registerOnChange(fn: (value: User) => void): void {
    this.control.valueChanges.subscribe(fn);
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  focus(index: number) {
    this.renderer.setAttribute(
      this.selectCoordinatorRef.nativeElement,
      'tabindex',
      index.toString()
    );
  }
}
